import React from 'react';
import styled from 'styled-components';

export interface TabItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
}

interface TabBarProps {
  tabs: TabItem[];
  activeTab: string;
  onTabChange: (id: string) => void;
  meta?: React.ReactNode;
  variant?: 'underline' | 'pills';
  ariaLabel?: string;
}

export const TabBar: React.FC<TabBarProps> = ({
  tabs,
  activeTab,
  onTabChange,
  meta,
  variant = 'underline',
  ariaLabel
}) => (
  <Bar $variant={variant} role="tablist" aria-label={ariaLabel}>
    {tabs.map((tab) => (
      <Tab
        key={tab.id}
        type="button"
        role="tab"
        aria-selected={activeTab === tab.id}
        $active={activeTab === tab.id}
        $variant={variant}
        onClick={() => onTabChange(tab.id)}
      >
        {tab.icon}
        {tab.label}
      </Tab>
    ))}
    {meta && <Meta>{meta}</Meta>}
  </Bar>
);

const Bar = styled.div<{ $variant: 'underline' | 'pills' }>`
  display: flex;
  align-items: center;
  gap: ${({ $variant }) => ($variant === 'pills' ? '4px' : '2px')};
  border: ${({ $variant }) =>
    $variant === 'pills' ? '1px solid var(--line-soft)' : 'none'};
  border-bottom: ${({ $variant }) =>
    $variant === 'underline' ? '1px solid var(--line)' : 'none'};
  border-radius: ${({ $variant }) => ($variant === 'pills' ? '999px' : '0')};
  background: ${({ $variant }) =>
    $variant === 'pills' ? 'var(--paper)' : 'transparent'};
  margin-bottom: ${({ $variant }) => ($variant === 'underline' ? '24px' : '0')};
  padding: ${({ $variant }) => ($variant === 'pills' ? '3px' : '0')};
  flex-shrink: 0;
`;

const Tab = styled.button<{
  $active: boolean;
  $variant: 'underline' | 'pills';
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${({ $variant }) =>
    $variant === 'pills' ? '6px 11px' : '10px 14px'};
  border: none;
  background: ${({ $active, $variant }) =>
    $variant === 'pills' && $active ? 'var(--white)' : 'none'};
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  color: ${({ $active, $variant }) =>
    $variant === 'pills'
      ? $active
        ? 'var(--ink)'
        : 'var(--ink-muted)'
      : $active
        ? 'var(--ink)'
        : 'var(--ink-muted)'};
  border-radius: ${({ $variant }) => ($variant === 'pills' ? '999px' : '0')};
  border-bottom: ${({ $active, $variant }) =>
    $variant === 'underline'
      ? `2px solid ${$active ? 'var(--terracotta)' : 'transparent'}`
      : 'none'};
  margin-bottom: ${({ $variant }) => ($variant === 'underline' ? '-1px' : '0')};
  box-shadow: ${({ $active, $variant }) =>
    $variant === 'pills' && $active ? 'var(--shadow-sm)' : 'none'};
  white-space: nowrap;
  transition: all 0.15s;

  &:hover {
    color: var(--ink);
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    opacity: ${({ $active }) => ($active ? 1 : 0.5)};
  }
`;

const Meta = styled.span`
  margin-left: auto;
  font-size: 11px;
  color: var(--ink-faint);
  font-weight: 500;
`;
